import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import type { FishGroupPopulationInfo } from '@/domain/fishGroups';
import { formatNumberMaxDecimalsByLocale } from '@/utils/localeFormatters';

import type { AllocationSummary as AllocationSummaryData } from './allocation';

import styles from './AllocationSummary.module.scss';

export interface BreakdownItem {
    /** Stable React key. */
    key: string;
    /** Already-localised label string (caller calls `t()`). */
    label: string;
    /** Population value to show on the right; null renders as "—". */
    population: number | null;
}

interface Props {
    summary: AllocationSummaryData;
    /** Sources to list individually (id + display name). */
    sources: { fishGroupId: number; name: string }[];
    populationById: Map<number, FishGroupPopulationInfo>;
    /**
     * Optional title override. Defaults to the generic "Population reference"
     * key, but each mode-step can pass a more specific label (e.g. "Splitting
     * population" vs "Combined sources").
     */
    titleKey?: string;
    /** What the source pool counts; the internal wizard's initial population by default. */
    disclaimerKey?: string;
    /**
     * Optional explicit breakdown items. When provided, the per-source
     * breakdown uses these directly instead of mapping `sources` through
     * `populationById`. Useful when the pool composition cannot be expressed
     * as raw initial populations — e.g. the split-and-merge target step,
     * where one item is "remaining from initial" (a derived figure) rather
     * than the full population of any single FG.
     */
    breakdownOverride?: BreakdownItem[];
}

/**
 * Mode-agnostic visual card that summarises:
 *  - The combined source population (with per-source breakdown if multiple).
 *  - The total currently allocated across the wizard's destinations.
 *  - Remaining or overage relative to the source pool.
 *
 * All four operation modes consume this same component — the helper
 * `computeAllocationSummary` produces uniform numbers regardless of mode.
 * Unknown populations are displayed as "—" so the operator knows the
 * reference is incomplete (the form remains usable either way).
 */
export default function AllocationSummary(props: Props) {
    const { summary, sources, populationById, titleKey, disclaimerKey, breakdownOverride } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const locale = i18n.language;

    // Build the breakdown items either from the explicit override (caller-
    // localised labels + caller-computed populations) or from the
    // sources × populationById fallback used by simpler steps.
    const breakdownItems: BreakdownItem[] =
        breakdownOverride ??
        sources.map((s) => ({
            key: String(s.fishGroupId),
            label: s.name,
            population: populationById.get(s.fishGroupId)?.initialPopulationSize ?? null,
        }));
    const showSourceBreakdown = breakdownItems.length > 1;

    return (
        <Card
            as="section"
            radius="sm"
            variant="subtle"
            padding="none"
            className={styles.allocationSummary}
            aria-live="polite">
            <header className={styles.allocationHeader}>
                <Overline>{t(titleKey ?? 'actions.operate.allocation.title')}</Overline>
                <span className={styles.allocationDisclaimer}>
                    {t(disclaimerKey ?? 'actions.operate.allocation.initialDisclaimer')}
                </span>
            </header>

            <dl className={styles.allocationGrid}>
                <dt>{t('actions.operate.allocation.sourcesLabel')}</dt>
                <dd>{formatTotal(summary.totalSourcePopulation, t, locale)}</dd>

                <dt>{t('actions.operate.allocation.allocatedLabel')}</dt>
                <dd>{formatNumberMaxDecimalsByLocale(summary.totalAllocated, locale)}</dd>

                {summary.remaining !== null && (
                    <>
                        <dt>{t('actions.operate.allocation.remainingLabel')}</dt>
                        <dd>{formatNumberMaxDecimalsByLocale(summary.remaining, locale)}</dd>
                    </>
                )}

                {summary.overage !== null && (
                    <>
                        <dt className={styles.allocationOverageLabel}>
                            {t('actions.operate.allocation.overageLabel')}
                        </dt>
                        <dd className={styles.allocationOverageValue}>
                            {formatNumberMaxDecimalsByLocale(summary.overage, locale)}
                        </dd>
                    </>
                )}
            </dl>

            {showSourceBreakdown && (
                <ul className={styles.allocationBreakdown}>
                    {breakdownItems.map((item) => (
                        <li key={item.key}>
                            <span className={styles.allocationBreakdownName}>{item.label}</span>
                            <span
                                className={classNames(styles.allocationBreakdownValue, {
                                    [styles['allocationBreakdownValue--unknown']]:
                                        item.population == null,
                                })}>
                                {formatTotal(item.population, t, locale)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

function formatTotal(value: number | null, t: (key: string) => string, locale: string): string {
    if (value === null) return t('actions.operate.allocation.unknown');
    return formatNumberMaxDecimalsByLocale(value, locale);
}
